import {
  enterActualsController,
  enterActualsTemplate
} from '../modals';

import moment from 'moment';
import formatCurrency from 'format-currency';

class GeneralBusinessActualsController {

  constructor(CompanyService, toastr, $scope, $http, $rootScope, $stateParams, $state, $uibModal, TravelExpenseService) {
    'ngInject';

    this.moment = moment;
    this.$http = $http;
    this.$rootScope = $rootScope;
    this.toastr = toastr;
    this.state = $state;
    this.$stateParams = $stateParams;
    this.$uibModal = $uibModal;
    this.report = { expenses: [] };
    this.travelExpenseService = TravelExpenseService;
    this.readyForSubmission = false;
    this.rejectionBaseline = null;
    this.apiHost = $rootScope.apiHost;

    this.paymentMethods = [
      { name: 'Corporate', value: 'corporate' },
      { name: 'Personal', value: 'personal' }
    ];

    this.loading = true;
    this.reloadExpenseReport();

    $scope.$watch(() => this.report, () => {
      this.updateReadyForSubmission();
    }, true);
  }

  getItems() {
    return (this.report.expenses || []).filter((expense) => !expense.itinerary);
  }

  getEstimatedTotal() {
    return this.getItems().reduce((total, expense) => total + (expense.amount || 0), 0);
  }

  getActualTotal() {
    return this.getItems().reduce((total, expense) => total + (expense.actualAmount || 0), 0);
  }

  formatDate(value) {
    return value ? moment(value).format('MMM DD, YYYY') : '';
  }

  buildChangeSnapshot(report) {
    const expenses = (report && report.expenses) ? report.expenses : [];

    return JSON.stringify(
      expenses
        .filter((expense) => !expense.itinerary)
        .map((expense) => ({
          id: expense.id || null,
          type: expense.type || null,
          description: expense.description || null,
          amount: expense.amount != null ? Number(expense.amount) : null,
          actualAmount: expense.actualAmount != null ? Number(expense.actualAmount) : null,
          paymentMethod: expense.paymentMethod || null,
          receiptIds: (expense.receipts || [])
            .map((receipt) => receipt.receipt)
            .filter(Boolean)
            .sort()
        }))
        .sort((a, b) => {
          const aId = a.id == null ? 0 : a.id;
          const bId = b.id == null ? 0 : b.id;
          return aId - bId;
        })
    );
  }

  isReimbursementRejected() {
    return this.report && this.report.status === 'REIMBURSEMENT_REJECTED';
  }

  hasUpdatesSinceRejection() {
    if (!this.isReimbursementRejected()) {
      return true;
    }

    if (!this.rejectionBaseline) {
      return false;
    }

    return this.buildChangeSnapshot(this.report) !== this.rejectionBaseline;
  }

  areExpensesReadyForSubmission() {
    const items = this.getItems();

    if (!items.length) {
      return false;
    }

    return items.every((e) =>
      e.actualAmount != null && e.actualAmount >= 0
      && e.receipts && e.receipts.length > 0
      && e.paymentMethod
    );
  }

  updateReadyForSubmission() {
    this.readyForSubmission = this.areExpensesReadyForSubmission() && this.hasUpdatesSinceRejection();
  }

  getSubmissionBlockedMessage() {
    if (this.isLocked() || this.readyForSubmission) {
      return '';
    }

    if (this.isReimbursementRejected() && !this.hasUpdatesSinceRejection()) {
      return 'Update the purchase items before resubmitting for reimbursement.';
    }

    return 'Enter actual amounts, payment methods, and receipts for every item before submitting for reimbursement.';
  }

  reloadExpenseReport(successToastMessage) {
    this.loading = true;
    return this.$http.get(this.$rootScope.apiHost + '/api/expense-reports/' + this.$stateParams.expenseReportId, { withCredentials: true }).then((success) => {
      this.loading = false;
      const report = this.travelExpenseService.normalizeExpenseReportDates(success.data);
      this.travelExpenseService.hydrateEstimateBasisDisplay(report);
      this.report = report;
      this.report.expenses = report.expenses || [];
      this.travelExpenseStatusDetails = this.travelExpenseService.getTravelExpenseStatusDetails(this.report.status);

      if (this.report.status === 'REIMBURSEMENT_REJECTED') {
        if (!this.rejectionBaseline) {
          this.rejectionBaseline = this.buildChangeSnapshot(this.report);
        }
      } else {
        this.rejectionBaseline = null;
      }

      this.updateReadyForSubmission();

      if (successToastMessage) {
        this.toastr.success(successToastMessage === true ? 'Actuals saved.' : successToastMessage, 'SUCCESS');
      }

      return report;
    }, () => {
      this.loading = false;
      this.toastr.error('Could not load expense report.', 'ERROR');
    });
  }

  openEnterActuals(expense) {
    if (this.isLocked() || !expense || !expense.id) {
      return;
    }

    const self = this;
    const modalInstance = this.$uibModal.open({
      template: enterActualsTemplate,
      controller: enterActualsController,
      controllerAs: '$ctrl',
      resolve: {
        expenseReport() {
          return self.report;
        },
        expense() {
          return expense;
        },
        paymentMethods() {
          return self.paymentMethods;
        }
      },
      windowClass: 'hmodal-success'
    });

    modalInstance.result.then(() => {
      self.reloadExpenseReport('Actuals saved.');
    });
  }

  hasEnteredActuals(expense) {
    return !!expense && expense.actualAmount != null && (expense.receipts && expense.receipts.length > 0);
  }

  getActualAmountDisplay(expense) {
    if (!expense || expense.actualAmount == null) {
      return '$ -';
    }

    return this.currency(expense.actualAmount) || '$ -';
  }

  getPaymentMethodLabel(expense) {
    if (!expense || !expense.paymentMethod) {
      return 'Not Set';
    }

    const match = this.paymentMethods.find((method) => method.value === expense.paymentMethod);
    return match ? match.name : expense.paymentMethod;
  }

  cancel() {
    this.state.go(this.isProxyImport() ? 'admin.expenses' : 'expenses');
  }

  isProxyImport() {
    return this.$stateParams.proxyImport === true
      || this.$stateParams.proxyImport === 'true'
      || this.travelExpenseService.isProxyImportEditable(this.report)
      || !!(this.report && this.report.proxyCreatedByUserId);
  }

  isLocked() {
    if (this.travelExpenseService.isProxyImportEditable(this.report)) {
      return false;
    }
    return !(this.report.status === 'APPROVED' || this.report.status === 'REIMBURSEMENT_REJECTED');
  }

  currency(value) {
    if (value != null && value !== '') {
      return '$' + formatCurrency(value);
    }
    return undefined;
  }

  submitForReimbursement() {
    if (!this.readyForSubmission) {
      this.toastr.warning(this.getSubmissionBlockedMessage() || 'Unable to submit yet.', 'Incomplete');
      return;
    }

    this.loading = true;
    if (this.report.id) {
      this.travelExpenseService.submitForReimbursement(this.report).then(() => {
        this.loading = false;
        this.state.go(this.isProxyImport() ? 'admin.expenses' : 'expenses');
      }, () => {
        this.loading = false;
        this.toastr.error('Could not submit for reimbursement.', 'ERROR');
      });
    }
  }
}

export default GeneralBusinessActualsController;



// WEBPACK FOOTER //
// ./client/pages/expenses/general-business-actuals/general-business-actuals.controller.js